import {
  Mapping,
  MappingField,
  MappingIdentifier,
  MappingIdentifierType,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import * as copy from 'src/templates/copy';
import { validateIri } from 'src/utils/validateIri';

export const isValidClassPrimaryIdentifier = (
  identifier: MappingIdentifier,
  fields: Record<string, MappingField>
) =>
  (identifier.baseIri === null ||
    identifier.baseIri === '' ||
    !validateIri(identifier.baseIri)) &&
  identifier.parts.some((part) => part.type === MappingIdentifierType.FIELD) &&
  !identifier.parts.some(
    (part) => part.type === MappingIdentifierType.FIELD && !fields[part.value]
  );

export const isValidRelationshipPrimaryIdentifier = (
  identifier: MappingIdentifier,
  fields: Record<string, MappingField>
) =>
  (identifier.baseIri === null ||
    identifier.baseIri === '' ||
    !validateIri(identifier.baseIri)) &&
  identifier.parts.length &&
  !identifier.parts.some(
    (part) => part.type === MappingIdentifierType.FIELD && !fields[part.value]
  );

export const isValidClassMapping = (mapping: Mapping) => {
  return !getReasonsForInvalidClassMapping(mapping);
};

export const getReasonsForInvalidClassMapping = (
  mapping: Mapping
): {
  reasons: string[];
  /** don't notify user about this because they probably already know it's not connected */
  quiet: boolean;
} | null => {
  const reasons: string[] = [];
  let quiet = false;

  if (!mapping.to) {
    reasons.push(copy.components.invalidMappings.validation.to(mapping.text));
    quiet = true;
  } else if (
    !isValidClassPrimaryIdentifier(mapping.primaryIdentifier, mapping.fields)
  ) {
    reasons.push(
      copy.components.invalidMappings.validation.primaryIdentifier(mapping.text)
    );
  }

  return reasons.length ? { reasons, quiet } : null;
};

export const isValidRelationshipMapping = (
  relationshipMapping: RelationshipMapping,
  mapping: Mapping | undefined
) => {
  return !getReasonsForInvalidRelationshipMapping(relationshipMapping, mapping);
};

export const getReasonsForInvalidRelationshipMapping = (
  relationshipMapping: RelationshipMapping,
  mapping: Mapping | undefined
): {
  reasons: string[];
  /** don't notify user about this because they probably already know it's not connected */
  quiet: boolean;
} | null => {
  const reasons: string[] = [];
  let quiet = false;

  if (!relationshipMapping.to && !relationshipMapping.from) {
    reasons.push(
      copy.components.invalidMappings.validation.fromOrTo(
        relationshipMapping.relationshipIri
      )
    );
    quiet = true;
  } else if (relationshipMapping.to && !relationshipMapping.fromClassIri) {
    reasons.push(
      copy.components.invalidMappings.validation.sourceClassIri(
        relationshipMapping.relationshipIri
      )
    );
    quiet = true;
  } else if (relationshipMapping.from && !relationshipMapping.toClassIri) {
    reasons.push(
      copy.components.invalidMappings.validation.targetClassIri(
        relationshipMapping.relationshipIri
      )
    );
    quiet = true;
  } else if (
    relationshipMapping.to &&
    (!relationshipMapping.fromPrimaryIdentifier ||
      !mapping ||
      !isValidRelationshipPrimaryIdentifier(
        relationshipMapping.fromPrimaryIdentifier,
        mapping.fields
      ))
  ) {
    reasons.push(
      copy.components.invalidMappings.validation.sourcePrimaryIdentifier(
        relationshipMapping.relationshipIri
      )
    );
    quiet = !relationshipMapping.fromPrimaryIdentifier;
  } else if (
    relationshipMapping.from &&
    (!relationshipMapping.toPrimaryIdentifier ||
      !mapping ||
      !isValidRelationshipPrimaryIdentifier(
        relationshipMapping.toPrimaryIdentifier,
        mapping.fields
      ))
  ) {
    reasons.push(
      copy.components.invalidMappings.validation.targetPrimaryIdentifier(
        relationshipMapping.relationshipIri
      )
    );
    quiet = !relationshipMapping.toPrimaryIdentifier;
  }

  return reasons.length ? { reasons, quiet } : null;
};
